import React, {lazy, Suspense} from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import {createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider} from "react-router-dom";
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {UserProvider} from "./context/UserProvider";
import LoadingWidget from './components/LoadingWidget';
import PageTransitionWrapper from "./context/PageTransitionWrapper";
import Header from "./components/Header";
import PrivateRoute from "./context/PrivateRoute";
import ThemeConfig from "./config/ThemeConfig";
import NotFound from "./pages/NotFound"; // Import the global page transition wrapper

// 🛑 Lazy Load Components for Performance Optimization
const Home = lazy(() => import("./pages/Home"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Login = lazy(() => import("./pages/Login"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseInfo = lazy(() => import("./pages/CourseInfo"));
const Cart = lazy(() => import("./pages/Cart"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const CourseMaterialOverview = lazy(() => import("./pages/CourseMaterialOverview"));
const CourseMaterial = lazy(() => import("./pages/CourseMaterial"));
const Admin = lazy(() => import("./pages/Admin"));
const ModifyMaterial = lazy(() => import("./pages/ModifyMaterial"));
const ModifyCourse = lazy(() => import("./pages/ModifyCourse"));
const UserScores = lazy(() => import("./pages/UserScores"));
const Meet = lazy(() => import("./pages/Meet"));
const ModifyService = lazy(() => import("./pages/ModifyService"));
const ServiceInfo = lazy(() => import("./pages/ServiceInfo"));
const Blog = lazy(() => import("./pages/Blog"));
const ModifyArticle = lazy(() => import("./pages/ModifyArticle"));
const Article = lazy(() => import("./pages/Article"));
const About = lazy(() => import("./pages/About"));
const AboutUser = lazy(() => import("./pages/AboutUser"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const Services = lazy(() => import("./pages/Services"));
const Certificate = lazy(() => import("./pages/Certificate"));
const ServiceOverview = lazy(() => import("./pages/ServiceOverview"));
const CourseDiscussion = lazy(() => import("./pages/CourseDiscussion"));

// 🏗 Layout Component for Shared UI Elements
const MainLayout = () => (
    <div>
        <Header/>
        <Outlet/> {/* This renders the child route components */}
    </div>
);
// 📌 Define Routes Using `createRoutesFromElements`
const router = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<MainLayout/>}>
            <Route path="/" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Home/></PageTransitionWrapper></Suspense>}/>
            <Route path="/sign-up" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><SignUp/></PageTransitionWrapper></Suspense>}/>
            <Route path="/sign-in" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Login/></PageTransitionWrapper></Suspense>}/>
            <Route path="/courses" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Courses/></PageTransitionWrapper></Suspense>}/>
            <Route path="/courses/:id" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><CourseInfo/></PageTransitionWrapper></Suspense>}/>
            <Route path="/cart" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Cart/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/dashboard" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><Dashboard/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/profile" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Profile/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/dashboard/courses/:id/materials" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/>
                </div>}><PageTransitionWrapper><CourseMaterialOverview/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/dashboard/courses/:id/discussion" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/>
                </div>}><PageTransitionWrapper><CourseDiscussion/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/dashboard/courses/:courseId/materials/:id" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><CourseMaterial/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/services" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Services/></PageTransitionWrapper></Suspense>}/>
            <Route path="/admin" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Admin/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/services/:id" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><ServiceInfo/></PageTransitionWrapper></Suspense>}/>
            <Route path="/:type/:id/certificate" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/>
                </div>}><PageTransitionWrapper><Certificate/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/courses/edit/:courseId/materials/:id" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><ModifyMaterial/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/courses/edit/:id" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><ModifyCourse/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/services/edit/:id" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><ModifyService/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/users/:id/scores" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><UserScores/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/users/:id" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><AboutUser/></PageTransitionWrapper></Suspense>}/>
            <Route path="/dashboard/services/:id" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/>
                </div>}><PageTransitionWrapper><ServiceOverview/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/payment-result" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><PaymentResult/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/blog" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Blog/></PageTransitionWrapper></Suspense>}/>
            <Route path="/about" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><About/></PageTransitionWrapper></Suspense>}/>
            <Route path="/blog/article/:id" element={<Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Article/></PageTransitionWrapper></Suspense>}/>
            <Route path="/blog/article/edit/:id" element={<PrivateRoute><Suspense
                fallback={<div className={"fullScreen"}><LoadingWidget/></div>}><PageTransitionWrapper><ModifyArticle/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="/meet" element={<PrivateRoute><Suspense fallback={<div className={"fullScreen"}><LoadingWidget/>
            </div>}><PageTransitionWrapper><Meet/></PageTransitionWrapper></Suspense></PrivateRoute>}/>
            <Route path="*" element={<NotFound />} />
        </Route>
    )
);

// 🔥 Render the App
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <UserProvider>
        <ThemeConfig>
            <DndProvider backend={HTML5Backend}>
                <RouterProvider router={router}/>
            </DndProvider>
        </ThemeConfig>
    </UserProvider>
);
